import { useState } from 'react';
import { Link } from 'react-router-dom';
import { STATUSES } from '../api/contentApi';
import { contentPath } from '../content/contentTypes';
import { META_DESCRIPTION_MAX, META_TITLE_MAX, imageBlocks, parseTags } from './changes';

function Field({ id, label, hint, error, children }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && !error && <p className="field-hint">{hint}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

function Counter({ value, max }) {
  const n = value.trim().length;
  return <span className={`counter${n > max ? ' over' : ''}`}>{n}/{max}</span>;
}

function Thumb({ url, alt }) {
  const [failedUrl, setFailedUrl] = useState(''); // remember which address failed, so a corrected address is tried again
  const address = url.trim();
  if (!address) return null;
  return failedUrl === address ? (
    <p className="field-error">This address does not show a picture.</p>
  ) : (
    <img className="thumb" src={address} alt={alt} onError={() => setFailedUrl(address)} />
  );
}

export default function ArticleEditor({ entry, doc, form, setForm, errors, dirty, saving, message, onSave, onRevert }) {
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setPicture = (id, key) => (e) =>
    setForm((f) => ({ ...f, pictures: { ...f.pictures, [id]: { ...f.pictures[id], [key]: e.target.value } } }));
  const pictures = imageBlocks(doc);
  const wasPublished = String(entry.status).toLowerCase() === 'published';
  const publishing = form.status === 'published' && !wasPublished;
  const hasErrors = Object.keys(errors).length > 0;
  const tags = parseTags(form.tags);

  return (
    <form className="editor" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
      <div className="editor-scroll">
        <section>
          <h2>Status</h2>
          <Field
            id="ed-status"
            label="Who can see this article"
            error={errors.status}
            hint={form.status === 'published' ? 'Visible to everyone on the website.' : 'Hidden from the website. Only visible here.'}
          >
            <select id="ed-status" value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
          {wasPublished && (
            <p className="field-hint">
              <Link to={contentPath({ type: doc.type, slug: entry.slug })} target="_blank">View on the website</Link>
            </p>
          )}
        </section>

        <section>
          <h2>Details</h2>
          <Field id="ed-tags" label="Tags" hint="Separate with commas.">
            <input id="ed-tags" value={form.tags} onChange={set('tags')} />
          </Field>
          {tags.length > 0 && (
            <ul className="tag-list preview-tags">
              {tags.map((t) => <li key={t}><span className="tag">{t}</span></li>)}
            </ul>
          )}
          <Field id="ed-category" label="Category">
            <input id="ed-category" value={form.categoryName} onChange={set('categoryName')} />
          </Field>
          <Field id="ed-author" label="Author">
            <input id="ed-author" value={form.authorName} onChange={set('authorName')} />
          </Field>
          <Field id="ed-excerpt" label="Summary (shown on the article card)">
            <textarea id="ed-excerpt" rows={3} value={form.excerpt} onChange={set('excerpt')} />
          </Field>
          <p className="field-hint">Web address: <code>/{doc.type === 'blog' ? 'blog' : doc.type}/{entry.slug}</code> (cannot be changed here)</p>
        </section>

        <section>
          <h2>Search engines</h2>
          <Field id="ed-mtitle" label={<>Meta title <Counter value={form.metaTitle} max={META_TITLE_MAX} /></>}>
            <input id="ed-mtitle" value={form.metaTitle} onChange={set('metaTitle')} />
          </Field>
          <Field id="ed-mdesc" label={<>Meta description <Counter value={form.metaDescription} max={META_DESCRIPTION_MAX} /></>}>
            <textarea id="ed-mdesc" rows={3} value={form.metaDescription} onChange={set('metaDescription')} />
          </Field>
          <Field id="ed-focus" label="Focus keyword">
            <input id="ed-focus" value={form.focusKeyword} onChange={set('focusKeyword')} />
          </Field>
        </section>

        <section>
          <h2>Featured picture</h2>
          <p className="field-hint">Shown on the article card and when the link is shared. Paste the web address of a picture that is already online.</p>
          <Field id="ed-fimg" label="Picture address" error={errors.featuredImageUrl}>
            <input id="ed-fimg" type="url" placeholder="https://…" value={form.featuredImageUrl} onChange={set('featuredImageUrl')} />
          </Field>
          <Field id="ed-falt" label="Describe the picture (alt text)">
            <input id="ed-falt" value={form.featuredImageAlt} onChange={set('featuredImageAlt')} />
          </Field>
          <Thumb url={form.featuredImageUrl} alt={form.featuredImageAlt} />
        </section>

        {pictures.length > 0 && (
          <section>
            <h2>Pictures in the article</h2>
            {pictures.map((p, i) => {
              const pic = form.pictures[p.mediaId];
              return (
                <div className="picture-item" key={p.mediaId}>
                  <p className="picture-title">Picture {i + 1}{p.caption ? ` — ${p.caption}` : ''}</p>
                  <Field id={`ed-pu-${i}`} label="Picture address" error={errors[`picture:${p.mediaId}`]}>
                    <input id={`ed-pu-${i}`} type="url" placeholder="https://…" value={pic.url} onChange={setPicture(p.mediaId, 'url')} />
                  </Field>
                  <Field id={`ed-pa-${i}`} label="Describe the picture (alt text)">
                    <input id={`ed-pa-${i}`} value={pic.alt} onChange={setPicture(p.mediaId, 'alt')} />
                  </Field>
                  <Thumb url={pic.url} alt={pic.alt} />
                </div>
              );
            })}
          </section>
        )}
      </div>

      <div className="editor-actions">
        {message && <p className={`save-message ${message.type}`} role="status">{message.text}</p>}
        <div className="editor-buttons">
          <button type="button" className="btn" onClick={onRevert} disabled={!dirty || saving}>Undo changes</button>
          <button type="submit" className="btn btn-primary" disabled={!dirty || saving || hasErrors}>
            {saving ? 'Saving…' : publishing ? 'Save and publish' : 'Save'}
          </button>
        </div>
        {dirty && !saving && <p className="field-hint">You have unsaved changes.</p>}
      </div>
    </form>
  );
}
